/**
 * MessagingArchitectureRenderer (T-061, ARCH-005 §1/§9, TECH-09, AI-02).
 *
 * Renders the Zod-validated messaging_architecture payload: a
 * purpose -> positioning -> narrative cascade visualization (T-061 AC) and a
 * per-audience message matrix. Body only.
 *
 * Verbosity (ARCH-005 §5): 'recommend' shows the cascade + audience headlines;
 * 'show_me' adds the narrative arc + proof points + CTAs;
 * 'build_it_with_me' adds the methodology callout.
 */

import {
  FieldList,
  MethodologyCallout,
  SectionHead,
  type RendererProps,
} from '../shared';
import type { MessagingArchitectureContent } from './payload';

function CascadeStep({
  index,
  label,
  body,
  isLast,
}: {
  index: number;
  label: string;
  body: string;
  isLast?: boolean;
}) {
  return (
    <div className="relative pl-8">
      <span className="absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--color-rose)] text-xs font-semibold text-[color:var(--color-navy)]">
        {index}
      </span>
      {!isLast ? (
        <span className="absolute left-3 top-6 h-[calc(100%-0.5rem)] w-px bg-[color:var(--color-rule)]" />
      ) : null}
      <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-mute)]">
        {label}
      </p>
      <p className="mb-4 text-sm text-[color:var(--color-navy)]">
        {body}
      </p>
    </div>
  );
}

export function MessagingArchitectureRenderer({
  payload,
  verbosity,
}: RendererProps<MessagingArchitectureContent>) {
  const full = verbosity !== 'recommend';
  return (
    <div className="space-y-6">
      <div>
        <SectionHead>The cascade</SectionHead>
        <div>
          <CascadeStep index={1} label="Purpose" body={payload.purpose} />
          <CascadeStep
            index={2}
            label="Positioning"
            body={payload.positioning}
          />
          <CascadeStep
            index={3}
            label="Narrative arc"
            body={payload.narrative_arc}
            isLast
          />
        </div>
      </div>

      <div>
        <SectionHead>Per-audience message</SectionHead>
        <div className="space-y-3">
          {payload.audience_matrix.map((m, i) => (
            <div
              key={i}
              className="rounded-md border border-[color:var(--color-rule)] p-3"
            >
              <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-mute)]">
                {m.audience}
              </p>
              <p className="font-medium text-[color:var(--color-navy)]">
                {m.headline}
              </p>
              {full ? (
                <div className="mt-1">
                  <FieldList title="Proof" items={m.proof_points} />
                </div>
              ) : null}
              {full && m.call_to_action ? (
                <p className="mt-1 text-sm text-[color:var(--color-text-mute)]">
                  CTA: {m.call_to_action}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <MethodologyCallout verbosity={verbosity}>
        <p>
          Read the cascade top to bottom. If positioning does not obviously
          follow from purpose, or a per-audience headline does not trace back to
          positioning, the chain has a break. Fix the break before the words.
        </p>
      </MethodologyCallout>
    </div>
  );
}
